import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { throwFromResponse } from '../lib/apiErrorMessage';

export interface Money {
  amount: number;
  currencyCode: string;
}

export interface CartSelectedOption {
  optionId: string | null;
  optionName: string | null;
  optionType: string | null;
  value: string | null;
  label: string | null;
  additionalAmount: number | null;
}

export interface UserCartItem {
  cartItemId: string;
  productId: string;
  productName: string | null;
  variantId: string | null;
  variantLabel: string | null;
  thumbnailUrl: string | null;
  quantity: number;
  unitPrice: Money;
  lineTotal: Money;
  addedAt: string;
  selectedOptions: ReadonlyArray<CartSelectedOption>;
}

export interface UserCart {
  userId: string;
  cartId: string | null;
  items: ReadonlyArray<UserCartItem>;
  itemCount: number;
  subTotal: Money;
}

interface UserCartEnvelope {
  success: boolean;
  data?: UserCart | null;
}

export const userCartQueryKey = (userId: string) =>
  ['admin', 'user-cart', userId] as const;

// 정본 routing: gateway `order-service-api-admin` route 가 /api/admin/v1/orders/** 를
// order-service 로 직접 라우팅. 본 cart endpoint 는 그 prefix 하위 /carts/{userId}.
export const useUserCartQuery = (userId: string) =>
  useQuery<UserCart>({
    queryKey: userCartQueryKey(userId),
    queryFn: async () => {
      const res = await adminFetch(`/api/admin/v1/orders/carts/${userId}`);
      if (!res.ok) await throwFromResponse(res, '장바구니를 불러오지 못했습니다.');
      const env = (await res.json()) as UserCartEnvelope;
      if (!env.data) throw new Error('장바구니 응답이 비어 있습니다.');
      return env.data;
    },
    enabled: Boolean(userId),
  });
